import {
    Box,
    IconButton,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    Typography,
} from "@mui/material";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useAppContext } from "../../../shared/contexts/app-context/components/AppContext";
import {
    useAppSidePanelContextProvider
} from "../../../shared/contexts/app-side-panel-context-provider/components/AppSidePanelContextProvider";
import { PortfolioGroupedItem } from "../models/PortfolioGroupedItem";
import { PortfolioItemModel } from "../../../features/portfolio-info/types/PortfolioItemModel";
import { assetService } from "../../../shared/services/AssetService";
import { blackScholesModelCalculationService } from "../../../shared/services/BlackScholesModelCalculationService";
import { dateTimeConversionService } from "../../../shared/services/DateTimeConversionService";
import { ContextMenu, PositionsToolsPanel } from "../controls";
import {
    ApplicationTooltip,
    LineDelimiter,
    PortfolioBalanceTooltipContent,
    PositionInfo,
    TableFooterRow,
} from "../views";
import { ContextMenuItem } from "../controls/context-menu/ContextMenuItem";
import { portfolioItemMappers } from "../../../features/portfolio-info/mappers/PortfolioItemMappers";
import { TableBodyRow, TableHeaderRow } from "../../shared/views";
import {
    POSITIONS_TABLE_ROW_PADDING_Y,
    TABLE_EXPIRATION_EXP_CELL_FONT_SIZE,
    TABLE_SYMBOL_EXP_CELL_FONT_SIZE
} from "../../shared/constants/table.constants";
import { numberFormatters } from "../../shared/utils/numberFormatters";
import { OrderState } from "../../../shared/types/OrderState";
import { positionsProcessor } from "../utils/PositionsProcessor";
import {
    PositionImpliedVolatilityChangingParameters
} from "../controls/position-implied-volatility-selector/PositionImpliedVolatilityChangingParameters";
import { PositionManagerDialog } from "../dialogs/position-manager-dialog/PositionManagerDialog";
import { customImpliedVolatilityInfoBuilder } from "../utils/CustomImpliedVolatilityInfoBuilder";
import EditIcon from '@mui/icons-material/Edit';
import { portfolioPerformanceService } from "../../../shared/services/PortfolioPerformanceService";

type PositionsProps = {
    isExpanded: boolean;
    setIsExpanded: Dispatch<SetStateAction<boolean>>;
};

export function Positions({
    isExpanded,
    setIsExpanded,
}: PositionsProps) {
    const {
        marketDataDto,
        selectedPortfolioBalance,
        setSelectedPortfolioBalance,
        selectedPortfolioVolume,
        setSelectedPortfolioVolume,
    } = useAppContext();
    
    const {
        selectedPortfolio,
        addFutureToPortfolio,
        updatePositionInSelectedPortfolio,
        saveCustomImpliedVolatility,
        updateSelectedPortfolio,
        saveSelectedPortfolio,
    } = useAppSidePanelContextProvider();
    
    const [hideClosedOrExpiredPositions, setHideClosedOrExpiredPositions] = useState<boolean>(true);
    const [selectedPosition, setSelectedPosition] = useState<PortfolioGroupedItem | null>(null);
    const [isSetCustomImpliedVolatilityDialogOpen, setIsSetCustomImpliedVolatilityDialogOpen] = useState<boolean>(false);

    const positions = selectedPortfolio !== null
        ? positionsProcessor.getSummarizedPortfolioItems(selectedPortfolio)
        : [];

    useEffect(() => {
        if (selectedPortfolio === null || selectedPortfolio.portfolioSettings === null) {
            setHideClosedOrExpiredPositions(true);
            return;
        }

        setHideClosedOrExpiredPositions(!selectedPortfolio.portfolioSettings.showClosedPositions);
    }, [selectedPortfolio]);

    const handleHideClosedOrExpiredPositionsChange = (value: SetStateAction<boolean>): void => {
        const nextHideClosedOrExpiredPositions = typeof value === 'function'
            ? value(hideClosedOrExpiredPositions)
            : value;

        setHideClosedOrExpiredPositions(nextHideClosedOrExpiredPositions);

        if (selectedPortfolio === null || selectedPortfolio.portfolioSettings === null) {
            return;
        }

        selectedPortfolio.portfolioSettings.showClosedPositions = !nextHideClosedOrExpiredPositions;

        void (async (): Promise<void> => {
            await updateSelectedPortfolio();
            await saveSelectedPortfolio();
        })();
    };

    const handleOnPositionClick = (position: PortfolioGroupedItem) => {
        setSelectedPosition(position);
    };

    const isCustomImpliedVolatilityApplicable = (position: PortfolioGroupedItem | null): boolean => {
        return (
            position !== null &&
            assetService.isGroupedOptions(position) &&
            position.currentSize !== 0 &&
            !assetService.isGroupedOptionsExpired(position)
        );
    };

    const handleEditPosition = (position: PortfolioGroupedItem) => {
        setSelectedPosition(position);
        setIsSetCustomImpliedVolatilityDialogOpen(true);
    };
    
    const handleAddClosingOrder = async (
        position: PortfolioGroupedItem,
        closeContextMenu: () => void
    ) => {
        closeContextMenu();
        
        if (
            marketDataDto === undefined ||
            selectedPortfolio === null || selectedPortfolio.portfolioSettings === null
        ) {
            return;
        }

        const portfolioSettings = selectedPortfolio!.portfolioSettings!;
        const underlyingPrice = portfolioSettings.useCustomPrice
            ? portfolioSettings.customPrice
            : marketDataDto.BaseAsset.Price ?? 0;

        const getOpenPrice = () => {
            if (assetService.isGroupedOptions(position)) {
                const timeToExpirationInShareOfYear = blackScholesModelCalculationService
                    .getTimeToExpirationInShareOfYear(position.expireAt!);

                return blackScholesModelCalculationService.getPremium(
                    position.type, underlyingPrice, position.strike!,
                    timeToExpirationInShareOfYear, position.impliedVolatility!);
            } else {
                return underlyingPrice;
            }
        }

        const openPrice = getOpenPrice();

        const newItemModel: PortfolioItemModel = new PortfolioItemModel({
            externalId: 0,
            exchangeOrderId: null,
            internalId: crypto.randomUUID(),
            state: OrderState.Active,
            isActive: true,
            locked: false,
            openedPositionAt: dateTimeConversionService.getCurrentUtcDate(),
            expireAt: position.expireAt,
            impliedVolatilityAtOpen: position.impliedVolatility,
            customImpliedVolatility: position.impliedVolatility,
            symbol: position.symbol,
            size: -position.currentSize,
            openPrice: openPrice,
            markPrice: openPrice,
            markImpliedVolatility: position.impliedVolatility,
            type: position.type,
            strike: position.strike,
            underlyingPriceAtOpen: underlyingPrice,
            delta: 0,
            gamma: null,
            vega: null,
            theta: null,
            comment: null,
            expirationPrice: position.expirationPrice,
            dateForPricing: dateTimeConversionService.getCurrentUtcDate(),
            impliedVolatilityForPricing: position.impliedVolatility,
            underlyingPriceForPricing: underlyingPrice,
        });

        await addFutureToPortfolio(newItemModel);
    };

    const portfolioBalance = positions
        .map(x => x.balance)
        .reduce((sum, value) => sum + value, 0);
    const performanceContext = selectedPortfolio !== null
        ? portfolioPerformanceService.createContext(positions, selectedPortfolio)
        : null;
    const portfolioVolume = performanceContext?.investedCapital ?? 0;

    setSelectedPortfolioBalance(portfolioBalance);
    setSelectedPortfolioVolume(portfolioVolume);

    const { roi, annualisedRoi } = performanceContext !== null
        ? portfolioPerformanceService.calculate(portfolioBalance, performanceContext)
        : { roi: null, annualisedRoi: null };
    
    const updatePositionInSelectedPortfolioWrapper = async (
        optionSymbol: string,
        customImpliedVolatility: number,
        customImpliedVolatilityStep?: number | null
    ): Promise<void> => {
        await updatePositionInSelectedPortfolio(optionSymbol, customImpliedVolatility, customImpliedVolatilityStep);
    }

    const handleSetCustomImpliedVolatility = async (customImpliedVolatility: number): Promise<void> => {
        if (selectedPosition === null || selectedPortfolio === null) {
            return;
        }

        await updatePositionInSelectedPortfolio(selectedPosition.symbol, customImpliedVolatility);

        const { positionsForUpdate, ordersForUpdate } =
            customImpliedVolatilityInfoBuilder.build(selectedPortfolio, selectedPosition.symbol);

        await saveCustomImpliedVolatility(positionsForUpdate, ordersForUpdate);
    };
    
    const buildPositionImpliedVolatilityChangingParameters = (): PositionImpliedVolatilityChangingParameters => {
        return {
            selectedPosition: selectedPosition,
            positions: positions,
            updatePositionInSelectedPortfolioWrapper: updatePositionInSelectedPortfolioWrapper,
        };
    }
    
    return (
        <Stack
            sx={{
                flex: 1,
                minHeight: 0,
                overflow: 'auto',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 0,
                paddingBottom: '8px',
            }}
        >
            <PositionManagerDialog
                isOpen={isSetCustomImpliedVolatilityDialogOpen}
                setIsOpen={setIsSetCustomImpliedVolatilityDialogOpen}
                position={selectedPosition}
                submitAction={handleSetCustomImpliedVolatility}
            />
            <PositionsToolsPanel
                hideClosedOrExpiredPositions={hideClosedOrExpiredPositions}
                setHideClosedOrExpiredPositions={handleHideClosedOrExpiredPositionsChange}
                isExpanded={isExpanded}
                setIsExpanded={setIsExpanded}
                positionImpliedVolatilityChangingParameters={buildPositionImpliedVolatilityChangingParameters()}
            />
            <LineDelimiter/>
            <Paper
                sx={{
                    flex: '1',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    borderRadius: 0,
                }}
            >
                <TableContainer
                    sx={{
                        flex: '1',
                        padding: '0px',
                        margin: '0px'
                    }}
                >
                    <Table
                        size="small"
                        stickyHeader
                        sx={{padding: '0px', margin: '0px', verticalAlign: 'text-top'}}
                    >
                        <TableHead>
                            <TableHeaderRow>
                                <ApplicationTooltip
                                    title="Edit position"
                                    disableInteractive
                                    offset={[0, -7]}
                                >
                                    <TableCell>E</TableCell>
                                </ApplicationTooltip>
                                <TableCell sx={{"&&":{textAlign: 'left'}}}>Symbol</TableCell>
                                <TableCell>Exp</TableCell>
                                <TableCell>Size</TableCell>
                                <ApplicationTooltip
                                    title="Average open price"
                                    disableInteractive
                                    offset={[0, -7]}
                                >
                                    <TableCell>Open</TableCell>
                                </ApplicationTooltip>
                                <ApplicationTooltip
                                    maxWidth="300px"
                                    title={
                                        <>
                                            <Box marginBottom={1}>
                                                Total invested money is calculated as the amount of capital required to open and maintain all positions.
                                            </Box>
                                            <Box marginBottom={0.5}>
                                                <b>Spot and options assets:</b> the actual amount paid to purchase the assets.
                                            </Box>
                                            <Box>
                                                <b>Futures assets:</b> the amount of capital required to maintain the position, assuming the price moves against it by the configured loss threshold.
                                            </Box>
                                        </>
                                    }
                                    disableInteractive
                                    offset={[0, -7]}
                                >
                                    <TableCell>Volume</TableCell>
                                </ApplicationTooltip>
                                <ApplicationTooltip
                                    title="Current market price"
                                    disableInteractive
                                    offset={[0, -7]}
                                >
                                    <TableCell>Close</TableCell>
                                </ApplicationTooltip>
                                <ApplicationTooltip
                                    title={
                                        <PortfolioBalanceTooltipContent
                                            roi={roi}
                                            annualisedRoi={annualisedRoi}
                                        />
                                    }
                                    disableInteractive
                                    offset={[0, -7]}
                                >
                                    <TableCell>Balance</TableCell>
                                </ApplicationTooltip>
                                <ApplicationTooltip
                                    title="Current implied volatility"
                                    disableInteractive
                                    offset={[0, -7]}
                                >
                                    <TableCell
                                        sx={{
                                            color: selectedPortfolio?.portfolioSettings?.isCustomImpliedVolatilityEnabled ?? false
                                                ? "rgba(0,0,0,0.4)"
                                                : "rgba(0,0,0,1)"
                                        }}
                                    >
                                        IV,%
                                    </TableCell>
                                </ApplicationTooltip>
                                <ApplicationTooltip
                                    title="Custom implied volatility"
                                    disableInteractive
                                    offset={[0, -7]}
                                >
                                    <TableCell
                                        sx={{
                                            color: selectedPortfolio?.portfolioSettings?.isCustomImpliedVolatilityEnabled ?? false
                                                ? "rgba(0,0,0,1)"
                                                : "rgba(0,0,0,0.4)"
                                        }}
                                    >
                                        CIV,%
                                    </TableCell>
                                </ApplicationTooltip>
                                <TableCell>Delta</TableCell>
                                <TableCell>Gamma</TableCell>
                                <TableCell>Vega</TableCell>
                                <TableCell>Theta</TableCell>
                            </TableHeaderRow>
                        </TableHead>
                        <TableBody>
                            {positions
                                .filter(position =>
                                    !hideClosedOrExpiredPositions ||
                                    (position.currentSize !== 0 &&
                                    !(assetService.isGroupedOptions(position) &&
                                        assetService.isGroupedOptionsExpired(position)))
                                )
                                .map((position) => (
                                    <ContextMenu<PortfolioGroupedItem>
                                        renderItems={(position, closeContextMenu) => (
                                            <>
                                                <ContextMenuItem
                                                    onClick={() => handleAddClosingOrder(position, closeContextMenu)}
                                                    disabled={position?.currentSize === 0}
                                                >
                                                    Add closing order
                                                </ContextMenuItem>
                                                <ContextMenuItem
                                                    onClick={() => {
                                                        closeContextMenu();
                                                        handleEditPosition(position);
                                                    }}
                                                    disabled={!isCustomImpliedVolatilityApplicable(position)}
                                                >
                                                    Set custom IV
                                                </ContextMenuItem>
                                            </>
                                        )}
                                    >
                                        {({onContextMenu}) => (
                                            <TableBodyRow
                                                onClick={() => handleOnPositionClick(position)}
                                                onContextMenu={
                                                    (event: React.MouseEvent) => {
                                                        setSelectedPosition(position);
                                                        onContextMenu(event, position)
                                                    }
                                                }
                                                key={portfolioItemMappers.getSymbolName(position.symbol, position.type)}
                                                paddingY={POSITIONS_TABLE_ROW_PADDING_Y}
                                                style={{
                                                    ...(position.currentSize === 0
                                                        ? { backgroundColor: 'rgba(236,255,229,0.67)' }
                                                        : {}),
                                                    ...(selectedPosition &&
                                                        selectedPosition.type === position.type &&
                                                        selectedPosition.symbol === position.symbol
                                                            ? position.currentSize !== 0
                                                                ? {
                                                                    backgroundColor: 'rgba(255,224,122,0.5)',
                                                                } : {
                                                                    backgroundColor: 'rgba(255,224,122,0.5)',
                                                                    backgroundImage: 'radial-gradient(rgba(215,155,20,0.3) 0.25px, transparent 1px)',
                                                                    backgroundSize: '4px 4px',
                                                                }
                                                            : {}),
                                                }}
                                            >
                                                <TableCell
                                                    align="center"
                                                    sx={{
                                                        "&&": {
                                                            paddingX: "2px",
                                                        },
                                                    }}
                                                >
                                                        <IconButton
                                                            color="warning"
                                                            size="small"
                                                            sx={{padding: '0px'}}
                                                            disabled={!isCustomImpliedVolatilityApplicable(position)}
                                                            onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                                                                event.stopPropagation();
                                                                handleEditPosition(position);
                                                            }}
                                                        >
                                                            <EditIcon/>
                                                        </IconButton>
                                                </TableCell>
                                                <ApplicationTooltip
                                                    title={<PositionInfo/>}
                                                    disableInteractive
                                                    offset={[0, -7]}
                                                >
                                                    <TableCell align="left">
                                                        <Typography
                                                            noWrap
                                                            sx={{
                                                                fontSize: TABLE_SYMBOL_EXP_CELL_FONT_SIZE,
                                                                textOverflow: 'ellipsis',
                                                            }}
                                                        >
                                                            {portfolioItemMappers.getSymbolName(position.symbol, position.type)}
                                                        </Typography>
                                                    </TableCell>
                                                </ApplicationTooltip>
                                                <TableCell align="right">
                                                    <Typography
                                                        noWrap
                                                        sx={{
                                                            fontSize: TABLE_EXPIRATION_EXP_CELL_FONT_SIZE,
                                                            textOverflow: 'ellipsis',
                                                        }}
                                                    >
                                                        {
                                                            assetService.isGroupedOptions(position) &&
                                                            !assetService.isGroupedOptionsExpired(position)
                                                                ? dateTimeConversionService.getExpirationPeriodInfoText(position.expireAt)
                                                                : null
                                                        }
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="right">
                                                    {numberFormatters.format3(position.currentSize)}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {numberFormatters.format0(position.averageOpenPrice)}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {numberFormatters.format0(position.volume)}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {
                                                        (assetService.isGroupedOptions(position) &&
                                                        assetService.isGroupedOptionsExpired(position)) ||
                                                        position.currentSize === 0
                                                            ? null
                                                            : numberFormatters.format0(position.markPrice)
                                                    }
                                                </TableCell>
                                                <TableCell align="right">
                                                    {numberFormatters.format2(position.balance)}
                                                </TableCell>
                                                <TableCell
                                                    align="right"
                                                    sx={{
                                                        color: selectedPortfolio?.portfolioSettings?.isCustomImpliedVolatilityEnabled ?? false
                                                            ? "rgba(0,0,0,0.4)"
                                                            : "rgba(0,0,0,1)"
                                                    }}
                                                >
                                                    {
                                                        assetService.isGroupedOptions(position) &&
                                                        !assetService.isGroupedOptionsExpired(position) &&
                                                        position.currentSize !== 0
                                                            ? position.impliedVolatility !== null
                                                                ? numberFormatters.format2(position.impliedVolatility * 100)
                                                                : ''
                                                            : null
                                                    }
                                                </TableCell>
                                                <TableCell
                                                    align="right"
                                                    sx={{
                                                        color: selectedPortfolio?.portfolioSettings?.isCustomImpliedVolatilityEnabled ?? false
                                                            ? "rgba(0,0,0,1)"
                                                            : "rgba(0,0,0,0.4)"
                                                    }}
                                                >
                                                    {
                                                        assetService.isGroupedOptions(position) &&
                                                        !assetService.isGroupedOptionsExpired(position) &&
                                                        position.currentSize !== 0
                                                            ? position.customImpliedVolatility !== null
                                                                ? numberFormatters.format2(position.customImpliedVolatility * 100)
                                                                : ''
                                                            : null
                                                    }
                                                </TableCell>
                                                <TableCell align="right">
                                                    {
                                                        !assetService.isGroupedOptionsExpired(position) &&
                                                        position.currentSize !== 0
                                                            ? numberFormatters.format3(position.delta)
                                                            : null
                                                    }
                                                </TableCell>
                                                <TableCell align="right">
                                                    {
                                                        position.gamma !== null &&
                                                        assetService.isGroupedOptions(position) &&
                                                        !assetService.isGroupedOptionsExpired(position) &&
                                                        position.currentSize !== 0
                                                            ? numberFormatters.format6(position.gamma)
                                                            : null
                                                    }
                                                </TableCell>
                                                <TableCell align="right">
                                                    {
                                                        position.vega !== null &&
                                                        assetService.isGroupedOptions(position) &&
                                                        !assetService.isGroupedOptionsExpired(position) &&
                                                        position.currentSize !== 0
                                                            ? numberFormatters.format2(position.vega)
                                                            : null
                                                    }
                                                </TableCell>
                                                <TableCell align="right">
                                                    {
                                                        position.theta !== null &&
                                                        assetService.isGroupedOptions(position) &&
                                                        !assetService.isGroupedOptionsExpired(position) &&
                                                        position.currentSize !== 0
                                                            ? numberFormatters.format0(position.theta)
                                                            : null
                                                    }
                                                </TableCell>
                                            </TableBodyRow>
                                        )}
                                    </ContextMenu>
                                ))}
                            <TableFooterRow>
                                <TableCell/>
                                <TableCell/>
                                <TableCell/>
                                <TableCell/>
                                <TableCell/>
                                <TableCell>
                                    {
                                        numberFormatters.format0(selectedPortfolioVolume)
                                    }
                                </TableCell>
                                <TableCell/>
                                <TableCell>
                                    {
                                        numberFormatters.format2(
                                            selectedPortfolioBalance -
                                            (selectedPortfolio && selectedPortfolio.portfolioSettings &&
                                            (selectedPortfolio.portfolioSettings.hideCommitedBalance ?? false) &&
                                            selectedPortfolio.portfolioSettings.commitedBalance
                                                ? selectedPortfolio.portfolioSettings?.commitedBalance
                                                : 0))
                                    }
                                </TableCell>
                                <TableCell/>
                                <TableCell/>
                                <TableCell>
                                    {numberFormatters.format3(positions
                                        .map(x => x.delta)
                                        .reduce((sum, value) => sum + value, 0))
                                    }
                                </TableCell>
                                <TableCell>
                                    {numberFormatters.format6(positions
                                        .map(x => x.gamma ?? 0)
                                        .reduce((sum, value) => sum + value, 0))
                                    }
                                </TableCell>
                                <TableCell>
                                    {numberFormatters.format2(positions
                                        .map(x => x.vega ?? 0)
                                        .reduce((sum, value) => sum + value, 0))
                                    }
                                </TableCell>
                                <TableCell>
                                    {numberFormatters.format0(positions
                                        .map(x => x.theta ?? 0)
                                        .reduce((sum, value) => sum + value, 0))
                                    }
                                </TableCell>
                            </TableFooterRow>
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        </Stack>
    );
}
